import React, { useState, useMemo, useCallback } from 'react';
import EstimatedArrivalContext from './EstimatedArrivalContext';
import { useLoading } from '@/utils/LoadingUtil';
import {
  getEstimatedArrivals,
  uploadETAFile,
  unloadETAFileBYId,
  downloadETAFile, // Added import
} from '@/services/EstimatedArrivalService';
import { handleApiError } from '@/lib/handleApiError';
import { showAlert } from '@/components';

const EstimatedArrivalContextProvider = ({ children }) => {
  const { isLoading, startLoading, stopLoading } = useLoading();
  const [estimatedArrivals, setEstimatedArrivals] = useState([]);
  const [totalElements, setTotalElements] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');

  const getEstimatedArrivalsAPI = useCallback(async () => {
    startLoading();
    try {
      const response = await getEstimatedArrivals(
        currentPage,
        pageSize,
        searchQuery,
      );
      console.log(response);
      setEstimatedArrivals(
        response._embedded?.eTAFileMasterDTOList || ['No Data Found'],
      );
      setTotalElements(response?.page?.totalElements);
    } catch (error) {
      handleApiError('error', error.response.data, error?.response?.status);
      setTotalElements(0);
      setEstimatedArrivals([]);
    } finally {
      stopLoading();
    }
  }, [currentPage, pageSize, searchQuery, startLoading, stopLoading]);

  const uploadETAFileAPI = async (file) => {
    startLoading();
    try {
      await uploadETAFile(file);
      getEstimatedArrivalsAPI();
    } catch (error) {
      //   showAlert("error", error.response.data);
    } finally {
      stopLoading();
    }
  };

  const unloadETAFileByIdAPI = async (id) => {
    startLoading();
    try {
      await unloadETAFileBYId(id);
      getEstimatedArrivalsAPI();
    } catch (error) {
      console.log(error);
      handleApiError('error', error.message, error?.response?.status);
    } finally {
      stopLoading();
    }
  };

  const downloadETAFileAPI = async (whseList) => {
    startLoading();
    try {
      const data = await downloadETAFile(whseList);
      console.log('File downloaded successfully', data);
    } catch (error) {
      console.log(error);
      handleApiError('error', error.message, error?.response?.status);
    } finally {
      stopLoading();
    }
  };

  const downloadETAFileReportAPI = async (whseList) => {
    startLoading();
    try {
      const data = await downloadETAFile(whseList);
      if (!data || data.size === 0) {
        throw new Error('No file available for download');
      }
      // Handle the downloaded data (e.g., save to file)
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'ETA_Report.csv'); // or any other extension
      document.body.appendChild(link);
      link.click();
      link.remove();
      console.log('File downloaded successfully');
    } catch (error) {
      console.log(error);
      handleApiError('error', error.message, error?.response?.status);
    } finally {
      stopLoading();
    }
  };

  const contextValue = useMemo(
    () => ({
      isLoading,
      startLoading,
      stopLoading,
      estimatedArrivals,
      getEstimatedArrivalsAPI,
      uploadETAFileAPI,
      unloadETAFileByIdAPI,
      totalElements,
      currentPage,
      pageSize,
      searchQuery,
      setCurrentPage,
      setPageSize,
      setSearchQuery,
      downloadETAFile, // Added to context value
      downloadETAFileAPI, // Added to context value
      downloadETAFileReportAPI, // Added to context value
    }),
    [
      isLoading,
      startLoading,
      stopLoading,
      estimatedArrivals,
      getEstimatedArrivalsAPI,
      uploadETAFileAPI,
      unloadETAFileByIdAPI,
      totalElements,
      currentPage,
      pageSize,
      searchQuery,
      downloadETAFile, // Added to dependency array
      downloadETAFileAPI, // Added to dependency array
      downloadETAFileReportAPI, // Added to dependency array
    ],
  );

  return (
    <EstimatedArrivalContext.Provider value={contextValue}>
      {children}
    </EstimatedArrivalContext.Provider>
  );
};

export default EstimatedArrivalContextProvider;
export const useEstimatedArrival = () =>
  React.useContext(EstimatedArrivalContext);
